import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import MarketHeader from "@/components/MarketHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Edit3, Play, Pause } from "lucide-react";
import { toast } from "sonner";

const emptyForm = () => ({
  name: "",
  index: "NIFTY",
  entry_time: "09:15:59",
  exit_time: "15:15:00",
  lot_size: 1,
  entry_limit_buffer_percent: 0,
  sl_limit_buffer_percent: 4,
  move_to_be_on_sl_hit: true,
  trade_mode: "paper",
  broker: "fyers",
  active: false,
  legs: [
    { kind: "CE", side: "SELL", delta_target: 40, sl_percent: 50 },
    { kind: "PE", side: "SELL", delta_target: 40, sl_percent: 50 },
  ],
});

export default function Strategies() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = () => api.get("/strategies").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);

  const openNew = () => { setEditingId(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({
      name: s.name, index: s.index, entry_time: s.entry_time, exit_time: s.exit_time,
      lot_size: s.lot_size,
      entry_limit_buffer_percent: s.entry_limit_buffer_percent ?? 0,
      sl_limit_buffer_percent: s.sl_limit_buffer_percent ?? 4,
      move_to_be_on_sl_hit: s.move_to_be_on_sl_hit,
      trade_mode: s.trade_mode, broker: s.broker || "fyers", active: s.active,
      legs: s.legs.map((l) => ({ ...l })),
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        lot_size: Number(form.lot_size),
        entry_limit_buffer_percent: Number(form.entry_limit_buffer_percent),
        sl_limit_buffer_percent: Number(form.sl_limit_buffer_percent),
      };
      if (editingId) await api.put(`/strategies/${editingId}`, payload);
      else await api.post("/strategies", payload);
      toast.success(editingId ? "Strategy updated" : "Strategy created");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const toggle = async (id) => {
    try { await api.patch(`/strategies/${id}/toggle`); load(); }
    catch { toast.error("Toggle failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this strategy?")) return;
    await api.delete(`/strategies/${id}`); load(); toast.success("Deleted");
  };

  const setLeg = (i, patch) => {
    const legs = [...form.legs];
    legs[i] = { ...legs[i], ...patch };
    setForm({ ...form, legs });
  };
  const addLeg = () => setForm({ ...form, legs: [...form.legs, { kind: "CE", side: "SELL", delta_target: 40, sl_percent: 50 }] });
  const removeLeg = (i) => setForm({ ...form, legs: form.legs.filter((_, ix) => ix !== i) });

  return (
    <div className="flex flex-col min-h-screen" data-testid="strategies-page">
      <MarketHeader />
      <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
          <div>
            <h1 className="font-heading text-xl sm:text-2xl font-bold tracking-tight">Strategies</h1>
            <p className="text-sm text-slate-400 mt-0.5">Time-based options strategies. Toggle to arm for auto-execution.</p>
          </div>
          <Button data-testid="new-strategy-btn" onClick={openNew} className="shrink-0 self-start bg-blue-500 hover:bg-blue-400 text-black font-semibold">
            <Plus className="w-4 h-4 mr-1.5" /> New Strategy
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.length === 0 && (
            <Card className="border-white/10 border-dashed bg-transparent p-8 text-center col-span-full" data-testid="no-strategies-card">
              <p className="text-slate-500 text-sm">No strategies yet. Create your first delta-based options strategy.</p>
            </Card>
          )}
          {items.map((s) => (
            <Card key={s.id} className="border-white/10 bg-[#141414] rounded-sm p-4 space-y-3" data-testid={`strategy-card-${s.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-heading font-semibold text-base">{s.name}</h3>
                  <div className="text-xs text-slate-500 mt-0.5 font-mono">{s.index} • entry {s.entry_time} • exit {s.exit_time}</div>
                </div>
                <span className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-widest ${s.trade_mode === "live" ? "bg-rose-500/15 text-rose-400" : "bg-amber-500/15 text-amber-400"}`}>{s.trade_mode}</span>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Broker <span className="text-slate-300 normal-case tracking-normal font-mono">{s.broker || "—"}</span></div>
              <div className="flex flex-wrap gap-1.5">
                {s.legs.map((l, i) => (
                  <span key={i} className="px-2 py-0.5 rounded-sm text-[11px] font-mono bg-white/5 border border-white/10">
                    {l.side} {l.kind} Δ{l.delta_target} SL {l.sl_percent}%
                  </span>
                ))}
              </div>
              <div className="text-xs text-slate-500">Lot × {s.lot_size} • Entry buffer {s.entry_limit_buffer_percent ?? 0}% • SL buffer {s.sl_limit_buffer_percent ?? 4}%</div>
              <div className="flex items-center justify-between border-t border-white/5 pt-3">
                <div className="flex items-center gap-2">
                  <Switch checked={s.active} onCheckedChange={() => toggle(s.id)} data-testid={`toggle-strategy-${s.id}`} />
                  <span className={`text-xs font-semibold ${s.active ? "text-emerald-400" : "text-slate-500"}`}>{s.active ? "ARMED" : "DISARMED"}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-strategy-${s.id}`}><Edit3 className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(s.id)} data-testid={`delete-strategy-${s.id}`} className="text-rose-400 hover:text-rose-300"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141414] border-white/10 max-w-2xl" data-testid="strategy-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit strategy" : "New strategy"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-slate-400">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="strategy-name-input" className="bg-[#0d0d0d] border-white/10 h-9" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-slate-400">Index</Label>
                <Select value={form.index} onValueChange={(v) => setForm({ ...form, index: v })}>
                  <SelectTrigger data-testid="strategy-index-select" className="bg-[#0d0d0d] border-white/10 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-white/10"><SelectItem value="NIFTY">NIFTY</SelectItem><SelectItem value="SENSEX">SENSEX</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-slate-400">Entry Time (HH:MM:SS IST)</Label>
                <Input value={form.entry_time} onChange={(e) => setForm({ ...form, entry_time: e.target.value })} data-testid="strategy-entry-time" className="bg-[#0d0d0d] border-white/10 h-9 font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-slate-400">Exit Time (HH:MM:SS IST)</Label>
                <Input value={form.exit_time} onChange={(e) => setForm({ ...form, exit_time: e.target.value })} data-testid="strategy-exit-time" className="bg-[#0d0d0d] border-white/10 h-9 font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-slate-400">Lot Multiplier</Label>
                <Input type="number" min="1" value={form.lot_size} onChange={(e) => setForm({ ...form, lot_size: e.target.value })} data-testid="strategy-lot-size" className="bg-[#0d0d0d] border-white/10 h-9 font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-slate-400">Entry Limit Buffer %</Label>
                <Input type="number" min="0" max="100" step="0.1" value={form.entry_limit_buffer_percent} onChange={(e) => setForm({ ...form, entry_limit_buffer_percent: e.target.value })} data-testid="strategy-entry-buffer" className="bg-[#0d0d0d] border-white/10 h-9 font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-slate-400">SL Limit Buffer %</Label>
                <Input type="number" min="0" max="100" step="0.1" value={form.sl_limit_buffer_percent} onChange={(e) => setForm({ ...form, sl_limit_buffer_percent: e.target.value })} data-testid="strategy-sl-buffer" className="bg-[#0d0d0d] border-white/10 h-9 font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-slate-400">Trade Mode</Label>
                <Select value={form.trade_mode} onValueChange={(v) => setForm({ ...form, trade_mode: v })}>
                  <SelectTrigger data-testid="strategy-mode-select" className="bg-[#0d0d0d] border-white/10 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-white/10"><SelectItem value="paper">Paper</SelectItem><SelectItem value="live">Live</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-slate-400">Broker</Label>
                <Select value={form.broker} onValueChange={(v) => setForm({ ...form, broker: v })}>
                  <SelectTrigger data-testid="strategy-broker-select" className="bg-[#0d0d0d] border-white/10 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-white/10">
                    <SelectItem value="fyers">Fyers</SelectItem>
                    <SelectItem value="zerodha">Zerodha</SelectItem>
                    <SelectItem value="upstox">Upstox</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2 border-t border-white/5 pt-3">
              <Switch checked={form.move_to_be_on_sl_hit} onCheckedChange={(v) => setForm({ ...form, move_to_be_on_sl_hit: v })} data-testid="strategy-be-toggle" />
              <span className="text-xs text-slate-300">When one leg SL hits, move other leg SL to Breakeven</span>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-widest text-slate-400">Legs</Label>
                <Button size="sm" variant="outline" onClick={addLeg} data-testid="add-leg-btn" className="border-white/10 h-7 text-xs">
                  <Plus className="w-3 h-3 mr-1" /> Add leg
                </Button>
              </div>
              <div className="space-y-2">
                {form.legs.map((leg, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end border border-white/10 rounded-sm p-2" data-testid={`leg-row-${i}`}>
                    <div className="col-span-2">
                      <Label className="text-[10px] uppercase tracking-widest text-slate-500">Kind</Label>
                      <Select value={leg.kind} onValueChange={(v) => setLeg(i, { kind: v })}>
                        <SelectTrigger className="bg-[#0d0d0d] border-white/10 h-8" data-testid={`leg-kind-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#141414] border-white/10"><SelectItem value="CE">CE</SelectItem><SelectItem value="PE">PE</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px] uppercase tracking-widest text-slate-500">Side</Label>
                      <Select value={leg.side} onValueChange={(v) => setLeg(i, { side: v })}>
                        <SelectTrigger className="bg-[#0d0d0d] border-white/10 h-8" data-testid={`leg-side-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#141414] border-white/10"><SelectItem value="SELL">SELL</SelectItem><SelectItem value="BUY">BUY</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px] uppercase tracking-widest text-slate-500">Delta target (0-100)</Label>
                      <Input type="number" min="1" max="100" value={leg.delta_target}
                             onChange={(e) => setLeg(i, { delta_target: Number(e.target.value) })}
                             data-testid={`leg-delta-${i}`} className="bg-[#0d0d0d] border-white/10 h-8 font-mono" />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px] uppercase tracking-widest text-slate-500">SL % of entry</Label>
                      <Input type="number" min="1" value={leg.sl_percent}
                             onChange={(e) => setLeg(i, { sl_percent: Number(e.target.value) })}
                             data-testid={`leg-sl-${i}`} className="bg-[#0d0d0d] border-white/10 h-8 font-mono" />
                    </div>
                    <div className="col-span-1">
                      <Button size="icon" variant="ghost" onClick={() => removeLeg(i)} className="text-rose-400 hover:text-rose-300 h-8 w-8" data-testid={`remove-leg-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setOpen(false)} className="border-white/10">Cancel</Button>
            <Button onClick={save} className="bg-blue-500 hover:bg-blue-400 text-black font-semibold" data-testid="strategy-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

